import { useEffect, useRef, useState } from 'react';
import { RelayControlCard } from './RelayControlCard';
import { MotorControlCard } from './MotorControlCard';
import { AutoModeCard } from './AutoModeCard';
import { useAttributes } from '@/hooks/useAttributes';
import { useRpc, useMotorRpc } from '@/hooks/useRpc';
import { useToast } from '@/hooks/useToast';
import {
  ALL_CONTROL_ATTRIBUTES,
  RELAY_CONFIG,
  MOTOR_CONFIG,
  MOTOR_COMMANDS,
} from '@/config/dataKeys';
import { RefreshCw, Clock, AlertTriangle, Moon, Sun } from 'lucide-react';
import { formatDateTime, normalizeBoolean, cn } from '@/lib/utils';
import { useT } from '@/i18n';
import { api } from '@/lib/api';

interface DashboardTabProps {
  project: string;
  gh: string;
  isReady: boolean;
  isOnline: boolean;
  userRole: string;
  /** ซ่อนการ์ด "โหมดทำงานตามเวลาอัตโนมัติ" — การ์ดนี้ผูกกับ RPC โหมด Auto ฝั่งบอร์ด
   * (fan_1_auto, valve_1_auto, ...) แบบตายตัว บอร์ดที่ไม่รองรับ RPC เหล่านี้
   * (เช่น เฟิร์มแวร์ที่มีแค่ set_{key}_cmd ธรรมดา) ควรซ่อนไว้ */
  hideAutoModeCard?: boolean;
}

type MotorCmd = number;
type OptimisticMotorState = Record<string, MotorCmd | null>;
type OptimisticRelayState = Record<string, boolean | null>;

const OPTIMISTIC_RELAY_TTL_MS = 8000;
const OPTIMISTIC_MOTOR_TTL_MS = 12000;

// ---- Types สำหรับ dynamic config ----
interface RelayConfigItem {
  key: string;
  name: string;
  icon: string;
  cmdKey: string;
  autoKey: string;
  rpcMethod: string;
}

interface MotorConfigItem {
  key: string;
  name: string;
  fwKey: string;
  reKey: string;
  rpcMethod: string;
}

export function DashboardTab({ project, gh, isReady, isOnline, userRole, hideAutoModeCard = false }: DashboardTabProps) {
  const { t } = useT();
  const { addToast } = useToast();

  // ---- Dynamic config state (fallback = hardcode) ----
  const [relayConfig, setRelayConfig] = useState<RelayConfigItem[]>([...RELAY_CONFIG]);
  const [motorConfig, setMotorConfig] = useState<MotorConfigItem[]>([...MOTOR_CONFIG]);

  useEffect(() => {
    if (!project || !gh) return;
    api.get<{ controls: any[] }>(`/projects/${project}/greenhouses/${gh}/control-configs`)
      .then((res) => {
        if (!res.success) return; // fetch จริงๆ ล้มเหลว (403/network) — ใช้ hardcode fallback ต่อไป
        const controls = res.data?.controls ?? [];

        const relays: RelayConfigItem[] = [];
        const motors: MotorConfigItem[] = [];

        for (const c of controls) {
          if (c.control_type === 'relay') {
            relays.push({
              key: c.control_key,
              name: c.name_th,
              icon: c.icon ?? 'Power',
              cmdKey: c.attribute_key,
              autoKey: c.auto_mode_key ?? '',
              rpcMethod: c.rpc_method,
            });
          } else if (c.control_type === 'motor') {
            const [fwKey, reKey] = (c.attribute_key ?? '').split(',');
            if (!fwKey || !reKey) continue; // skip ถ้า format ผิด
            motors.push({
              key: c.control_key,
              name: c.name_th,
              fwKey: fwKey.trim(),
              reKey: reKey.trim(),
              rpcMethod: c.rpc_method,
            });
          }
        }

        // โปรเจคที่ใช้เฟิร์มแวร์แบบ simplified (เช่น tomato) ตั้งค่าตาม DB เสมอแม้ว่างเปล่า
        // เพื่อซ่อน section ที่ไม่มีจริง (เช่น ไม่มีมอเตอร์พรางแสง)
        // แต่โปรเจคอื่นที่ยังไม่เคยตั้งค่า control_configs เลย (ว่างทั้ง relay และ motor)
        // ให้ใช้ hardcode fallback ต่อ ไม่งั้นปุ่มควบคุมแมนนวลหายทั้งหน้า
        if (relays.length === 0 && motors.length === 0 && !hideAutoModeCard) return;
        setRelayConfig(relays);
        setMotorConfig(motors);
      })
      .catch(() => {
        // เงียบ — ใช้ fallback hardcode ต่อไป
      });
  }, [project, gh, hideAutoModeCard]);

  const { data, isLoading, refetch, lastUpdated } = useAttributes({
    project,
    gh,
    keys: ALL_CONTROL_ATTRIBUTES,
    enabled: isReady && isOnline,
    pollInterval: 5000,
  });

  const refetchTimerRef = useRef<number | null>(null);
  const scheduleRefetch = (delayMs = 0) => {
    if (refetchTimerRef.current) window.clearTimeout(refetchTimerRef.current);
    refetchTimerRef.current = window.setTimeout(() => {
      refetch();
      refetchTimerRef.current = null;
    }, delayMs);
  };
  useEffect(() => {
    return () => { if (refetchTimerRef.current) window.clearTimeout(refetchTimerRef.current); };
  }, []);

  const [optimisticRelays, setOptimisticRelays] = useState<OptimisticRelayState>({});
  const relayTtlRef = useRef<Record<string, number>>({});

  const clearRelayOptimistic = (relayKey: string) => {
    const t = relayTtlRef.current[relayKey];
    if (t) window.clearTimeout(t);
    delete relayTtlRef.current[relayKey];
    setOptimisticRelays((prev) => {
      if (!(relayKey in prev)) return prev;
      const next = { ...prev };
      next[relayKey] = null;
      return next;
    });
  };

  const setRelayOptimistic = (relayKey: string, value: boolean, relayName: string) => {
    setOptimisticRelays((prev) => ({ ...prev, [relayKey]: value }));
    const old = relayTtlRef.current[relayKey];
    if (old) window.clearTimeout(old);
    relayTtlRef.current[relayKey] = window.setTimeout(() => {
      // TTL หมดโดยไม่มีการยืนยันค่าจริงจากบอร์ด (attribute ไม่เปลี่ยนภายในเวลานี้) —
      // ต่างจากตอน RPC ส่งไม่สำเร็จ (มี toast อยู่แล้วที่ handleRelayToggle) เคสนี้คือ
      // ส่งคำสั่งไปถึง ThingsBoard สำเร็จ แต่บอร์ดไม่ยืนยันกลับมา (ออฟไลน์จริง/บัสไม่เสถียร)
      addToast({ type: 'error', message: `${relayName}: ไม่ได้รับการยืนยันจากบอร์ด (เชื่อมต่อไม่เสถียรหรือบอร์ดไม่ตอบสนอง)` });
      clearRelayOptimistic(relayKey);
    }, OPTIMISTIC_RELAY_TTL_MS);
  };

  useEffect(() => {
    for (const relay of relayConfig) {
      const opt = optimisticRelays[relay.key];
      if (opt === null || opt === undefined) continue;
      if (data[relay.cmdKey] === undefined) continue;
      const real = normalizeBoolean(data[relay.cmdKey]);
      if (real === opt) clearRelayOptimistic(relay.key);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (isReady && isOnline) return;
    setOptimisticRelays({});
    Object.values(relayTtlRef.current).forEach((t) => window.clearTimeout(t));
    relayTtlRef.current = {};
  }, [isReady, isOnline]);

  useEffect(() => {
    return () => {
      Object.values(relayTtlRef.current).forEach((t) => window.clearTimeout(t));
      relayTtlRef.current = {};
    };
  }, []);

  const [optimisticMotors, setOptimisticMotors] = useState<OptimisticMotorState>({});
  const motorTtlRef = useRef<Record<string, number>>({});

  const clearMotorOptimistic = (motorKey: string) => {
    const t = motorTtlRef.current[motorKey];
    if (t) window.clearTimeout(t);
    delete motorTtlRef.current[motorKey];
    setOptimisticMotors((prev) => {
      if (!(motorKey in prev)) return prev;
      const next = { ...prev };
      next[motorKey] = null;
      return next;
    });
  };

  const setMotorOptimistic = (motorKey: string, cmd: MotorCmd) => {
    setOptimisticMotors((prev) => ({ ...prev, [motorKey]: cmd }));
    const old = motorTtlRef.current[motorKey];
    if (old) window.clearTimeout(old);
    motorTtlRef.current[motorKey] = window.setTimeout(
      () => clearMotorOptimistic(motorKey),
      OPTIMISTIC_MOTOR_TTL_MS
    );
  };

  useEffect(() => {
    for (const motor of motorConfig) {
      const opt = optimisticMotors[motor.key];
      if (opt === null || opt === undefined) continue;
      if (data[motor.fwKey] === undefined || data[motor.reKey] === undefined) continue;
      const realFw = normalizeBoolean(data[motor.fwKey]);
      const realRe = normalizeBoolean(data[motor.reKey]);
      let realCmd = MOTOR_COMMANDS.STOP;
      if (realFw && !realRe) realCmd = MOTOR_COMMANDS.FORWARD as 0;
      else if (!realFw && realRe) realCmd = MOTOR_COMMANDS.REVERSE as 0;
      if (realCmd === opt) clearMotorOptimistic(motor.key);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (isReady && isOnline) return;
    setOptimisticMotors({});
    Object.values(motorTtlRef.current).forEach((t) => window.clearTimeout(t));
    motorTtlRef.current = {};
  }, [isReady, isOnline]);

  useEffect(() => {
    return () => {
      Object.values(motorTtlRef.current).forEach((t) => window.clearTimeout(t));
      motorTtlRef.current = {};
    };
  }, []);

  const rpc = useRpc({
    project,
    gh,
    onSuccess: () => addToast({ type: 'success', message: t('dashboard.cmdSent') }),
    onError: (_method, error) => addToast({ type: 'error', message: error }),
  });

  const motorRpc = useMotorRpc({
    project,
    gh,
    onSuccess: () => addToast({ type: 'success', message: t('dashboard.motorCmdSent') }),
    onError: (_method, error) => addToast({ type: 'error', message: error }),
  });

  const canControl = userRole === 'superadmin' || userRole === 'admin' || userRole === 'operator';
  const controlsDisabled = !isReady || !isOnline || !canControl;
  const globalAuto = normalizeBoolean(data['global_motor_auto']);

  const handleRelayToggle = (relayKey: string, rpcMethod: string, relayName: string, next: boolean) => {
    setRelayOptimistic(relayKey, next, relayName);
    rpc.sendCommand(rpcMethod, next ? 1 : 0, next).then((ok) => {
      if (!ok) {
        clearRelayOptimistic(relayKey);
        addToast({ type: 'error', message: t('dashboard.relayCmdFailed').replace('{name}', relayName) });
      }
    });
  };

  const handleMotorCommand = (motorKey: string, rpcMethod: string, motorName: string, cmd: number) => {
    setMotorOptimistic(motorKey, cmd);
    motorRpc.sendMotorCommand(motorKey, rpcMethod, cmd).then((ok) => {
      if (!ok) {
        clearMotorOptimistic(motorKey);
        addToast({ type: 'error', message: t('dashboard.motorCmdFailed').replace('{name}', motorName) });
      }
    });
  };

  return (
    <div className="space-y-6">
      {!canControl && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            {t('dashboard.noControlPermission')}
          </p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Clock className="w-4 h-4" />
          <span>{t('dashboard.lastUpdate')}: {lastUpdated ? formatDateTime(new Date(lastUpdated)) : '--'}</span>
        </div>
        <button
          onClick={() => scheduleRefetch(0)}
          disabled={isLoading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-primary hover:bg-primary/5 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          {t('common.refresh')}
        </button>
      </div>

      {/* Auto Mode */}
      {!hideAutoModeCard && (
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
          <span className="w-1.5 h-5 bg-purple-500 rounded-full"></span>
          {t('dashboard.autoModeTitle')}
        </h2>
        <AutoModeCard
          data={data}
          isLoading={isLoading}
          isReady={isReady}
          disabled={controlsDisabled}
          onToggle={(method, value) => rpc.sendCommand(method, value ? 1 : 0, value)}
          isPending={rpc.isPending}
        />
      </section>
      )}

      {/* Relays */}
      {relayConfig.length > 0 && (
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
          <span className="w-1.5 h-5 bg-accent rounded-full"></span>
          {t('dashboard.relayTitle')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {relayConfig.map((relay) => {
            const realIsOn = normalizeBoolean(data[relay.cmdKey]);
            const isAuto = normalizeBoolean(data[relay.autoKey]);
            const effectiveIsOn = optimisticRelays[relay.key] ?? realIsOn;

            return (
              <RelayControlCard
                key={relay.key}
                name={relay.name}
                icon={relay.icon}
                isOn={effectiveIsOn}
                isAuto={isAuto}
                isLoading={isLoading}
                isReady={isReady}
                disabled={controlsDisabled || isAuto}
                onToggle={() => handleRelayToggle(
                  relay.key,
                  relay.rpcMethod,
                  relay.name,
                  !effectiveIsOn
                )}
              />
            );
          })}
        </div>
      </section>
      )}

      {/* Motors */}
      {motorConfig.length > 0 && (
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
          <span className="w-1.5 h-5 bg-orange-500 rounded-full"></span>
          {t('dashboard.motorTitle')}
        </h2>

        <div className="mb-4 flex gap-3">
          <button
            onClick={() => motorConfig.forEach((motor) =>
              handleMotorCommand(motor.key, motor.rpcMethod, motor.name, MOTOR_COMMANDS.FORWARD)
            )}
            disabled={controlsDisabled || globalAuto}
            className={cn(
              'flex-1 py-4 px-6 rounded-xl font-bold text-base transition-all duration-300 shadow-lg',
              'flex items-center justify-center gap-3',
              'disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-95',
              'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 shadow-blue-200'
            )}
          >
            <Moon className="w-6 h-6" />
            <span>{t('dashboard.shadeAll')}</span>
          </button>

          <button
            onClick={() => motorConfig.forEach((motor) =>
              handleMotorCommand(motor.key, motor.rpcMethod, motor.name, MOTOR_COMMANDS.REVERSE)
            )}
            disabled={controlsDisabled || globalAuto}
            className={cn(
              'flex-1 py-4 px-6 rounded-xl font-bold text-base transition-all duration-300 shadow-lg',
              'flex items-center justify-center gap-3',
              'disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-95',
              'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700 shadow-orange-200'
            )}
          >
            <Sun className="w-6 h-6" />
            <span>{t('dashboard.unshadeAll')}</span>
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {motorConfig.map((motor) => {
            const isFw = normalizeBoolean(data[motor.fwKey]);
            const isRe = normalizeBoolean(data[motor.reKey]);

            return (
              <MotorControlCard
                key={motor.key}
                name={motor.name}
                isFw={isFw}
                isRe={isRe}
                isAuto={globalAuto}
                isLoading={isLoading}
                isReady={isReady}
                disabled={controlsDisabled || globalAuto}
                isPending={motorRpc.isMotorPending(motor.key)}
                optimisticCmd={optimisticMotors[motor.key] ?? null}
                onCommand={(cmd) => handleMotorCommand(motor.key, motor.rpcMethod, motor.name, cmd)}
              />
            );
          })}
        </div>
      </section>
      )}
    </div>
  );
}